import { createI18nProxy } from '@/utils/i18nEnum';
export enum ModuleTypeEnum {
    CARD_WRITING = 1, // 와글와글
    WORD_CHECK = 2, // 단어 추리하기
    WORD_WRITING = 3, // 받아쓰기
    WORD_SELECTION = 4, // 단어 고르기
    SEED_LETTER = 5, // 씨앗글자
    OX_QUIZ = 6, // OX 퀴즈
    FILL_IN_THE_BLANKS = 7, // 빈칸 채우기
    PRO_CON_DEBATE = 8, // 찬반 토론
    SEGMENTED_READING = 9, // 차근차근 읽기
    SENTENCE_ORDERING = 10, // 순서 맞추기
    CORRECT_THE_MISTAKES = 11, // 고쳐 쓰기
    POST_IT = 12, // 표 완성하기
    MAGIC_WORD_TEST = 13, // 알아요! 몰라요?
    TALK_QUIZ = 14, // 퀴즈다냥
}

/**
 * [_moduleTypeKeys]
 * 각 모듈 타입(ModuleTypeEnum)에 대응하는 다국어 번역 키(i18n key)를 매핑한 내부용 객체입니다.
 *
 * 실제 한글/영문 텍스트가 아닌 'enums.components...' 형식의 키를 저장하며,
 * 이 객체는 moduleTypeLabel Proxy의 원본 데이터(target)로 사용됩니다.
 */

const _moduleTypeKeys: Record<ModuleTypeEnum, string> = {
    [ModuleTypeEnum.CARD_WRITING]: 'enums.components.modules.common.moduleLabel.cardWriting',
    [ModuleTypeEnum.WORD_CHECK]: 'enums.components.modules.common.moduleLabel.wordCheck',
    [ModuleTypeEnum.WORD_WRITING]: 'enums.components.modules.common.moduleLabel.wordWriting',
    [ModuleTypeEnum.WORD_SELECTION]: 'enums.components.modules.common.moduleLabel.wordSelection',
    [ModuleTypeEnum.SEED_LETTER]: 'enums.components.modules.common.moduleLabel.seedLetter',
    [ModuleTypeEnum.OX_QUIZ]: 'enums.components.modules.common.moduleLabel.oxQuiz',
    [ModuleTypeEnum.FILL_IN_THE_BLANKS]: 'enums.components.modules.common.moduleLabel.fillInTheBlanks',
    [ModuleTypeEnum.PRO_CON_DEBATE]: 'enums.components.modules.common.moduleLabel.proConDebate',
    [ModuleTypeEnum.SEGMENTED_READING]: 'enums.components.modules.common.moduleLabel.segmentedReading',
    [ModuleTypeEnum.SENTENCE_ORDERING]: 'enums.components.modules.common.moduleLabel.sentenceOrdering',
    [ModuleTypeEnum.CORRECT_THE_MISTAKES]: 'enums.components.modules.common.moduleLabel.correctTheMistakes',
    [ModuleTypeEnum.POST_IT]: 'enums.components.modules.common.moduleLabel.postIt',
    [ModuleTypeEnum.MAGIC_WORD_TEST]: 'enums.components.modules.common.moduleLabel.magicWordTest',
    [ModuleTypeEnum.TALK_QUIZ]: 'enums.components.modules.common.moduleLabel.talkQuiz',
};

export const moduleTypeThumbnail: Record<ModuleTypeEnum, string> = {
    [ModuleTypeEnum.CARD_WRITING]: 'thumb_card_writing',
    [ModuleTypeEnum.WORD_CHECK]: 'thumb_word_check',
    [ModuleTypeEnum.WORD_WRITING]: 'thumb_word_writing',
    [ModuleTypeEnum.WORD_SELECTION]: 'thumb_word_selection',
    [ModuleTypeEnum.SEED_LETTER]: 'thumb_seed_letter',
    [ModuleTypeEnum.OX_QUIZ]: 'thumb_ox_quiz',
    [ModuleTypeEnum.FILL_IN_THE_BLANKS]: 'thumb_fill_in_the_blanks',
    [ModuleTypeEnum.PRO_CON_DEBATE]: 'thumb_pro_con_debate',
    [ModuleTypeEnum.SEGMENTED_READING]: 'thumb_segmented_reading',
    [ModuleTypeEnum.SENTENCE_ORDERING]: 'thumb_sentence_ordering',
    [ModuleTypeEnum.CORRECT_THE_MISTAKES]: 'thumb_correct_the_mistakes',
    [ModuleTypeEnum.POST_IT]: 'thumb_post_it',
    [ModuleTypeEnum.MAGIC_WORD_TEST]: 'thumb_magic_word_test',
    [ModuleTypeEnum.TALK_QUIZ]: 'thumb_talk_quiz',
};

export const moduleTypeColor: Record<ModuleTypeEnum, string> = {
    [ModuleTypeEnum.CARD_WRITING]: 'danger',
    [ModuleTypeEnum.WORD_CHECK]: 'primary',
    [ModuleTypeEnum.WORD_WRITING]: 'primary',
    [ModuleTypeEnum.WORD_SELECTION]: 'primary',
    [ModuleTypeEnum.SEED_LETTER]: 'warning',
    [ModuleTypeEnum.OX_QUIZ]: 'info',
    [ModuleTypeEnum.FILL_IN_THE_BLANKS]: 'success',
    [ModuleTypeEnum.PRO_CON_DEBATE]: 'warning',
    [ModuleTypeEnum.SEGMENTED_READING]: 'warning',
    [ModuleTypeEnum.SENTENCE_ORDERING]: 'success',
    [ModuleTypeEnum.CORRECT_THE_MISTAKES]: 'info',
    [ModuleTypeEnum.POST_IT]: 'danger',
    [ModuleTypeEnum.MAGIC_WORD_TEST]: 'primary',
    [ModuleTypeEnum.TALK_QUIZ]: 'primary',
};

/**
 * 성취도(점수) 산출 가능한 활동 유형.
 * Backend `ModuleTypeEnum::hasScore()` 와 일치해야 한다.
 */
export const scoreModuleTypes: ReadonlySet<ModuleTypeEnum> = new Set([
    ModuleTypeEnum.WORD_WRITING,
    ModuleTypeEnum.WORD_SELECTION,
    ModuleTypeEnum.SEED_LETTER,
    ModuleTypeEnum.OX_QUIZ,
    ModuleTypeEnum.FILL_IN_THE_BLANKS,
    ModuleTypeEnum.PRO_CON_DEBATE,
    ModuleTypeEnum.SENTENCE_ORDERING,
    ModuleTypeEnum.CORRECT_THE_MISTAKES,
    ModuleTypeEnum.POST_IT,
    ModuleTypeEnum.MAGIC_WORD_TEST,
    ModuleTypeEnum.TALK_QUIZ,
]);

// 미리보기 구현된 모듈 타입
export const previewModuleTypes = [
    ModuleTypeEnum.CARD_WRITING, // 와글와글
    ModuleTypeEnum.WORD_CHECK, // 단어 추리하기
    ModuleTypeEnum.WORD_WRITING, // 받아쓰기
    ModuleTypeEnum.WORD_SELECTION, // 단어 고르기
    ModuleTypeEnum.SEED_LETTER, // 씨앗글자
    ModuleTypeEnum.CORRECT_THE_MISTAKES, // 고쳐 쓰기
    ModuleTypeEnum.FILL_IN_THE_BLANKS, // 빈칸 채우기
    ModuleTypeEnum.SEGMENTED_READING, // 차근차근 읽기
    ModuleTypeEnum.SENTENCE_ORDERING, // 순서 맞추기
    ModuleTypeEnum.OX_QUIZ, // OX 퀴즈
    ModuleTypeEnum.POST_IT, // 표 완성하기
    ModuleTypeEnum.MAGIC_WORD_TEST, // 알아요! 몰라요?
    ModuleTypeEnum.TALK_QUIZ, // 퀴즈다냥
];

// SVG Sprite 아이콘 ID 매핑
export const moduleTypeSpriteIcon: Record<ModuleTypeEnum, string> = {
    [ModuleTypeEnum.CARD_WRITING]: 'ico_card_writing',
    [ModuleTypeEnum.WORD_CHECK]: 'ico_word_check',
    [ModuleTypeEnum.WORD_WRITING]: 'ico_word_writing',
    [ModuleTypeEnum.WORD_SELECTION]: 'ico_word_selection',
    [ModuleTypeEnum.SEED_LETTER]: 'ico_seed_letter',
    [ModuleTypeEnum.OX_QUIZ]: 'ico_ox_quiz',
    [ModuleTypeEnum.FILL_IN_THE_BLANKS]: '', // sprite에 없음
    [ModuleTypeEnum.PRO_CON_DEBATE]: 'ico_pro_con_debate',
    [ModuleTypeEnum.SEGMENTED_READING]: 'ico_segmented_reading',
    [ModuleTypeEnum.SENTENCE_ORDERING]: 'ico_sentence_ordering',
    [ModuleTypeEnum.CORRECT_THE_MISTAKES]: 'ico_correct_the_mistakes',
    [ModuleTypeEnum.POST_IT]: 'ico_post_it',
    [ModuleTypeEnum.MAGIC_WORD_TEST]: 'ico_magic_word_test',
    [ModuleTypeEnum.TALK_QUIZ]: 'ico_talk_quiz',
};

export function getModuleThumbnailPath(type: ModuleTypeEnum): string {
    try {
        return `/assets/img/module/thumb/${moduleTypeThumbnail[type]}.svg`;
    } catch {
        return 'https://placehold.co/480x300';
    }
}

/** SVG Sprite 아이콘 ID 반환 */
export function getModuleSpriteIcon(type: ModuleTypeEnum): string {
    return moduleTypeSpriteIcon[type] || '';
}

/** SVG Sprite를 사용하는지 여부 */
export function useModuleSpriteIcon(type: ModuleTypeEnum): boolean {
    return !!moduleTypeSpriteIcon[type];
}

// 모듈별 svg 아이콘
export function moduleTypeSvgIcon(type: ModuleTypeEnum): string {
    switch (type) {
        case ModuleTypeEnum.CARD_WRITING:
            return 'ico_card_writing';
        case ModuleTypeEnum.WORD_CHECK:
            return 'ico_word_check';
        case ModuleTypeEnum.WORD_WRITING:
            return 'ico_word_writing';
        case ModuleTypeEnum.WORD_SELECTION:
            return 'ico_word_selection';
        case ModuleTypeEnum.SEED_LETTER:
            return 'ico_seed_letter';
        case ModuleTypeEnum.OX_QUIZ:
            return 'ico_ox_quiz';
        case ModuleTypeEnum.FILL_IN_THE_BLANKS:
            return 'ico_fill_in_the_blanks';
        case ModuleTypeEnum.PRO_CON_DEBATE:
            return 'ico_pro_con_debate';
        case ModuleTypeEnum.SEGMENTED_READING:
            return 'ico_segmented_reading';
        case ModuleTypeEnum.SENTENCE_ORDERING:
            return 'ico_sentence_ordering';
        case ModuleTypeEnum.CORRECT_THE_MISTAKES:
            return 'ico_correct_the_mistakes';
        case ModuleTypeEnum.POST_IT:
            return 'ico_post_it';
        case ModuleTypeEnum.MAGIC_WORD_TEST:
            return 'ico_magic_word_test';
        case ModuleTypeEnum.TALK_QUIZ:
            return 'ico_talk_quiz';
        default:
            return 'ico_segmented_reading';
    }
}

/**
 * [moduleTypeLabel]
 * 모듈 타입별 라벨을 제공하는 다국어 대응용 Proxy 객체입니다.
 *
 * [특징]
 * 1. 실시간 번역: moduleTypeLabel[ModuleTypeEnum.OX_QUIZ]와 같이 접근하면 현재 언어 설정에 맞는 텍스트를 반환합니다.
 * 2. 하위 호환성: 기존의 정적 객체와 동일한 방식으로 사용 가능하여 기존 템플릿 코드를 수정할 필요가 없습니다.
 * 3. 안정성: Symbol.iterator 등 객체 내부 심볼 접근을 허용하여 Element Plus 등의 외부 라이브러리에서 순회(Iteration) 시 발생하는 오류를 방지합니다.
 */
export const moduleTypeLabel = createI18nProxy(_moduleTypeKeys);
